import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider';
import { Save, Loader2, Bell, Shield } from 'lucide-react';

export function SettingsPage() {
 const { user } = useAuth();
 const { toast } = useToast();
 const [isSaving, setIsSaving] = useState(false);
 const [settings, setSettings] = useState({
  emailNotifications: true,
  interviewReminders: true,
  weeklyProgress: false,
  marketingEmails: false,
 });

 const handleSave = async () => {
  try {
   setIsSaving(true);
   await new Promise((resolve) => setTimeout(resolve, 1000));
   toast({
    title: 'Settings Updated',
    description: 'Your preferences have been saved.',
   });
  } catch (error) {
   toast({
    title: 'Error',
    description: 'Failed to save settings.',
    variant: 'destructive',
   });
  } finally {
   setIsSaving(false);
  }
 };

 return (
  <AppLayout>
   {/* Header */}
   <div className='mb-12 border-b border-border pb-8'>
    <div className='flex items-center gap-2 mb-2'>
     <div className='w-2 h-2 bg-primary rounded-full' />
     <span className='font-mono text-xs text-primary'>PREFERENCES</span>
    </div>
    <h1 className='text-3xl font-bold tracking-tight mb-2'>SETTINGS</h1>
    <p className='text-muted-foreground font-mono text-sm'>
     Manage your account information and application preferences.
    </p>
   </div>

   <div className='grid gap-8 max-w-4xl'>
    {/* Account Settings */}
    <div className='border border-border bg-background p-8'>
     <div className='flex items-center gap-2 mb-8 border-b border-border/50 pb-4'>
      <Shield className='h-5 w-5 text-primary' />
      <h3 className='font-mono text-sm font-bold'>SECURITY & ACCOUNT</h3>
     </div>

     <div className='space-y-6'>
      <div className='grid md:grid-cols-2 gap-8'>
       <div className='space-y-3'>
        <Label
         htmlFor='email'
         className='font-mono text-xs text-muted-foreground'
        >
         EMAIL ADDRESS
        </Label>
        <Input
         id='email'
         type='email'
         value={user?.email || ''}
         disabled
         className='rounded-none font-mono bg-muted/50'
        />
       </div>
       <div className='space-y-3'>
        <Label
         htmlFor='fullName'
         className='font-mono text-xs text-muted-foreground'
        >
         FULL NAME
        </Label>
        <Input
         id='fullName'
         type='text'
         defaultValue={user?.fullName || ''}
         className='rounded-none font-mono'
        />
       </div>
      </div>
      <div className='pt-4'>
       <Button
        variant='outline'
        size='sm'
        className='rounded-none font-mono text-xs'
       >
        RESET PASSWORD
       </Button>
      </div>
     </div>
    </div>

    {/* Notification Settings */}
    <div className='border border-border bg-background p-8'>
     <div className='flex items-center gap-2 mb-8 border-b border-border/50 pb-4'>
      <Bell className='h-5 w-5 text-primary' />
      <h3 className='font-mono text-sm font-bold'>NOTIFICATIONS</h3>
     </div>

     <div className='space-y-6'>
      <div className='flex items-center justify-between p-4 border border-border/50 hover:border-primary/50 transition-colors bg-muted/10'>
       <div className='space-y-1'>
        <Label
         htmlFor='emailNotifications'
         className='font-bold font-mono text-sm'
        >
         EMAIL UPDATES
        </Label>
        <p className='text-xs text-muted-foreground font-mono'>
         Receive updates about your account activity.
        </p>
       </div>
       <Switch
        id='emailNotifications'
        checked={settings.emailNotifications}
        onCheckedChange={(checked) =>
         setSettings({ ...settings, emailNotifications: checked })
        }
       />
      </div>

      <div className='flex items-center justify-between p-4 border border-border/50 hover:border-primary/50 transition-colors bg-muted/10'>
       <div className='space-y-1'>
        <Label
         htmlFor='interviewReminders'
         className='font-bold font-mono text-sm'
        >
         SESSION REMINDERS
        </Label>
        <p className='text-xs text-muted-foreground font-mono'>
         Get notified before scheduled sessions.
        </p>
       </div>
       <Switch
        id='interviewReminders'
        checked={settings.interviewReminders}
        onCheckedChange={(checked) =>
         setSettings({ ...settings, interviewReminders: checked })
        }
       />
      </div>

      <div className='flex items-center justify-between p-4 border border-border/50 hover:border-primary/50 transition-colors bg-muted/10'>
       <div className='space-y-1'>
        <Label htmlFor='weeklyProgress' className='font-bold font-mono text-sm'>
         WEEKLY DIGEST
        </Label>
        <p className='text-xs text-muted-foreground font-mono'>
         Receive a summary of your weekly progress.
        </p>
       </div>
       <Switch
        id='weeklyProgress'
        checked={settings.weeklyProgress}
        onCheckedChange={(checked) =>
         setSettings({ ...settings, weeklyProgress: checked })
        }
       />
      </div>

      <div className='flex items-center justify-between p-4 border border-border/50 hover:border-primary/50 transition-colors bg-muted/10'>
       <div className='space-y-1'>
        <Label
         htmlFor='marketingEmails'
         className='font-bold font-mono text-sm'
        >
         PRODUCT UPDATES
        </Label>
        <p className='text-xs text-muted-foreground font-mono'>
         Receive news about new features and improvements.
        </p>
       </div>
       <Switch
        id='marketingEmails'
        checked={settings.marketingEmails}
        onCheckedChange={(checked) =>
         setSettings({ ...settings, marketingEmails: checked })
        }
       />
      </div>
     </div>
    </div>

    {/* Save Button */}
    <div className='flex justify-end pt-4'>
     <Button
      onClick={handleSave}
      disabled={isSaving}
      className='rounded-none font-mono px-8'
     >
      {isSaving ? (
       <>
        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
        SAVING...
       </>
      ) : (
       <>
        <Save className='h-4 w-4 mr-2' />
        SAVE CHANGES
       </>
      )}
     </Button>
    </div>
   </div>
  </AppLayout>
 );
}
